import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { HFP_INSTRUMENTS } from '../utils/referralStatus';

/**
 * The organisation's HFP instrument setting (`rms_organisations/{orgId}.hfpInstrument`,
 * set with admin/rms/set_org_hfp_instrument.py): the generation every profile
 * that has not started yet opens on (TDI-523). It is the second step of the
 * one browser resolver, hfpInstrumentForRecord(patient, orgSetting); a record
 * whose profile has started carries its own pin and never needs it.
 *
 * Live: the 16 Nov switch to v4 reaches every open page without a reload. The
 * organisation document is readable by any signed-in user of that
 * organisation (firestore.rules). Returns null until the first snapshot, when
 * the setting is absent or invalid, or when the read fails, and the resolver
 * then falls back to the pathway default exactly as the server does.
 *
 * @param {string|null|undefined} orgId
 * @returns {'v2'|'v3'|'v4'|null}
 */
export default function useOrgHfpInstrument(orgId) {
  const [setting, setSetting] = useState(null);

  useEffect(() => {
    if (!orgId) {
      setSetting(null);
      return undefined;
    }
    let unsubscribe;
    try {
      unsubscribe = onSnapshot(
        doc(db, 'rms_organisations', orgId),
        (snap) => {
          try {
            const data = snap && snap.exists() ? snap.data() || {} : {};
            const meta = data.meta && typeof data.meta === 'object' ? data.meta : {};
            const value = data.hfpInstrument || meta.hfpInstrument || null;
            setSetting(HFP_INSTRUMENTS.includes(value) ? value : null);
          } catch (_e) {
            setSetting(null);
          }
        },
        () => setSetting(null),
      );
    } catch (_e) {
      setSetting(null);
    }
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [orgId]);

  return setting;
}
